import React, { useMemo, useState } from 'react';
import { motion } from 'motion/react';
import {
  getLivePrice, getTradeLock, getRowImage, getStickerPct, describeStickerPct,
  buildStickerIndex, withStickerImages,
} from './helpers';
import {
  ZONE, CANDY, COND_ABBR, getItemTypes, readableInk, fmt, signed, pct, rise,
  ZoneHead, ZoneStats, ZoneSearch, ZoneChips, ZoneSelect, ZoneEmpty, ZoneFooter,
} from './zoneShared';
import { TradePartnerBadge } from './TradePartner';
import ItemDetailModal, { DetailButton, TradeButton, hasTrade } from './ItemDetailModal';
import useGroupPick from './useGroupPick';
import StickerSlots from './StickerSlots';
import { isFlipRow } from './investments';
import { isLent } from './lending';
import { useSettling, settlesInLabel } from './settlingSales';

// Live Zone — the Armory (Live 2) wearing the Overview 7 board look.
// Same data and same click-to-edit behaviour as Live 2.
const SORTS = [
  { key: 'value',    label: 'Highest cost' },
  { key: 'delta',    label: 'Biggest gain' },
  { key: 'deltaAsc', label: 'Biggest loss' },
  { key: 'newest',   label: 'Newest first' },
  { key: 'name',     label: 'Name A-Z' },
];

export default function LiveTabZone({
  rows, settings, livePrices, csfloatPrices, skinsData = [], inventoryMap, onEdit,
  onBulkAdd, tradePartners, onGroupPrice, onSetCategory,
  lendingEnabled, onLendItems, onMoveToInvestment,
}) {
  const [query, setQuery] = useState('');
  const [cat, setCat]     = useState('all');
  const [sort, setSort]   = useState('value');
  const [detail, setDetail] = useState(null);   // the item whose modal is open
  // Ticking cards feeds three different actions, so one picker is shared and
  // this decides what the bar at the bottom offers. Switching mode drops the
  // selection: a set picked to be priced together is rarely the set you want to
  // tag or send somewhere else.
  const [mode, setMode] = useState(null);       // 'price' | 'move' | 'type' | null

  const settlingOf = useSettling();

  const holdings = useMemo(() => {
    return (rows || [])
      .map((row, realIndex) => ({ row, realIndex }))
      // Investments are the Vault's, not the Armory's — one item, one home.
      .filter(({ row }) => isFlipRow(row))
      .map(e => {
        const buy  = parseFloat(e.row.buyPrice) || 0;
        const lp   = getLivePrice(livePrices, e.row);
        const mkt  = lp?.starting_at?.price ?? null;
        const diff = mkt != null ? mkt - buy : null;
        const type = getItemTypes(e.row)
          .map(t => settings?.itemTypes?.find(it => it.value === t.value))
          .find(Boolean);
        const inv  = e.row.assetId ? inventoryMap?.get(e.row.assetId) : null;
        // Sold on CSFloat and waiting out trade protection. The row stays here
        // until the sale is verified (see settlingSales.js), but it is no longer
        // an item you are waiting to be able to sell, so it is not "locked".
        const settling = settlingOf(e.row);
        const lock = getTradeLock(e.row, inv);
        const locked = settling ? false : lock.locked;
        const until  = lock.until;
        // Crafts only: what percentage of the sticker value the price paid
        // covered, once the bare skin is taken out of it.
        const sp = getStickerPct(e.row, livePrices);
        return { ...e, buy, mkt, diff, type, locked, until, settling, sp };
      });
  }, [rows, livePrices, settings, inventoryMap, settlingOf]);

  const cats = useMemo(() => {
    const present = new Set();
    holdings.forEach(h => getItemTypes(h.row).forEach(t => present.add(t.value)));
    return (settings?.itemTypes || []).filter(t => present.has(t.value));
  }, [holdings, settings]);

  // Rows built from the trade history carry sticker names but no pictures, so
  // the strip would draw five empty numbered boxes. Built once: the database is
  // 40k entries and this would otherwise be walked per sticker, per card.
  const stickerIndex = useMemo(() => buildStickerIndex(skinsData), [skinsData]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const list = holdings.filter(h => {
      if (q && !h.row.skin.value.toLowerCase().includes(q)) return false;
      if (cat !== 'all' && !getItemTypes(h.row).some(t => t.value === cat)) return false;
      return true;
    });
    const by = {
      value:    (a, b) => b.buy - a.buy,
      delta:    (a, b) => (b.diff ?? -Infinity) - (a.diff ?? -Infinity),
      deltaAsc: (a, b) => (a.diff ?? Infinity) - (b.diff ?? Infinity),
      newest:   (a, b) => new Date(b.row.timestamp) - new Date(a.row.timestamp),
      name:     (a, b) => a.row.skin.value.localeCompare(b.row.skin.value),
    }[sort];
    return [...list].sort(by);
  }, [holdings, query, cat, sort]);

  // Items out on loan are not on this page any more, but the money still is —
  // it sits in your portfolio totals, just not in the Armory's. Without saying
  // so, lending a knife looks exactly like losing one: the count drops, the
  // cash spent drops, and nothing on screen explains where either went.
  const lentOut = useMemo(() => {
    const out = (rows || []).filter(r => isLent(r) && r.skin?.value);
    return { count: out.length, cost: out.reduce((s, r) => s + (parseFloat(r.buyPrice) || 0), 0) };
  }, [rows]);

  const totals = useMemo(() => {
    const cost = holdings.reduce((s, h) => s + h.buy, 0);
    let value = 0, unreal = 0, priced = 0;
    holdings.forEach(h => {
      if (h.mkt != null) { value += h.mkt; unreal += h.mkt - h.buy; priced++; }
      else value += h.buy;
    });
    return { cost, value, unreal, priced, locked: holdings.filter(h => h.locked).length };
  }, [holdings]);

  // Group pricing: tick several cards, then split one amount across them.
  // The same picker drives Move, see `mode` above.
  // Called before the empty-state return so the hook order never changes.
  const gp = useGroupPick(filtered, holdings);
  const pick = m => { const next = mode === m ? null : m; setMode(next); gp.setOn(!!next); };

  // A row can only go somewhere it is not already. Counting the ones that
  // cannot move keeps the bar honest about what a click will actually do.
  // Investments no longer reach this list at all, so every pick can move to the
  // Vault — only the lend count still has anything to exclude.
  const movable = {
    lend:   gp.picked.entries.filter(x => !x.row.lend).length,
    invest: gp.picked.count,
  };

  // How many of the picked items already carry each category. This is what
  // makes one pill do both jobs: a category everything already has is taken
  // off, anything else is put on, so a half-tagged selection is levelled up
  // rather than flipped item by item.
  const tagged = {};
  gp.picked.entries.forEach(e =>
    getItemTypes(e.row).forEach(t => { if (t?.value) tagged[t.value] = (tagged[t.value] || 0) + 1; }));

  const applyType = t => {
    const n = tagged[t.value] || 0;
    onSetCategory(gp.picked.entries.map(e => e.realIndex), t, n === gp.picked.count ? 'remove' : 'add');
  };

  if (!holdings.length) {
    return (
      <div className="zn-root">
        <ZoneHead pre="THE" hl="ARMORY" sub="Everything you currently hold, laid out on the board." />
        <ZoneEmpty>
          Nothing in the armory yet.
          <div style={{ marginTop: 16, display: 'flex', gap: 10, justifyContent: 'center', flexWrap: 'wrap' }}>
            <button className="zn-btn primary" onClick={onBulkAdd}>Bulk add</button>
          </div>
        </ZoneEmpty>
      </div>
    );
  }

  const roi = totals.cost > 0 ? totals.unreal / totals.cost * 100 : null;

  return (
    <div className="zn-root">

      <ZoneHead
        pre="THE" hl="ARMORY"
        sub="Every item you're still holding, priced against the live market. Click any card to edit it."
        stickers={[
          { text: `${holdings.length} items held`, bg: ZONE.teal },
          { text: livePrices ? 'prices: LIVE ✷' : 'prices: loading…', bg: ZONE.lime },
          totals.locked > 0 && { text: `${totals.locked} trade locked`, bg: ZONE.orange },
          lentOut.count > 0 && { text: `${lentOut.count} out on loan`, bg: ZONE.purple },
        ]}
      />

      <ZoneStats items={[
        { label: 'Cash spent',   value: `$${fmt(totals.cost)}`,
          sub: lentOut.count > 0
            ? `cost of what's in hand · $${fmt(lentOut.cost)} more is out on loan`
            : 'cost of everything held',
          bg: ZONE.white },
        { label: 'Market value', value: `$${fmt(totals.value)}`, sub: `${totals.priced} of ${holdings.length} have a live price`, bg: ZONE.yellow },
        { label: 'Paper gains',  value: signed(totals.unreal),
          sub: roi != null ? `${pct(roi)} on cost` : 'no live prices yet',
          bg: totals.unreal >= 0 ? ZONE.teal : ZONE.pink },
      ]} />

      {/* ── Toolbar ────────────────────────────────── */}
      <div className="zn-bar">
        <ZoneSearch value={query} onChange={setQuery} placeholder="Search your items…" />
        <ZoneChips
          options={[{ key: 'all', label: 'All' }, ...cats.map(t => ({ key: t.value, label: t.label, color: t.color }))]}
          value={cat} onChange={setCat} allowToggleOff
        />
        <span className="zn-count" style={{ marginLeft: 'auto' }}>
          {filtered.length} of {holdings.length}
        </span>
        <ZoneSelect value={sort} onChange={setSort} options={SORTS} label="Sort items" />

        {/* Item actions — the toolbar that used to live on the old Live tab */}
        <span className="zn-bar-actions">
          {onGroupPrice && (
            <button className={`zn-btn${mode === 'price' ? ' primary' : ''}`} onClick={() => pick('price')}
              title="Select several items and split one purchase price across them">
              {mode === 'price' ? 'Cancel select' : 'Group price'}
            </button>
          )}
          {onSetCategory && (settings?.itemTypes || []).length > 0 && (
            <button className={`zn-btn${mode === 'type' ? ' primary' : ''}`} onClick={() => pick('type')}
              title="Select several items and tag them all with a category">
              {mode === 'type' ? 'Cancel category' : 'Set category'}
            </button>
          )}
          {(onMoveToInvestment || (lendingEnabled && onLendItems)) && (
            <button className={`zn-btn${mode === 'move' ? ' primary' : ''}`} onClick={() => pick('move')}
              title="Select items and send them to lending or investments">
              {mode === 'move' ? 'Cancel move' : 'Move'}
            </button>
          )}
          <button className="zn-btn" onClick={onBulkAdd}>Bulk add</button>
        </span>
      </div>

      {/* ── Grid ───────────────────────────────────── */}
      {filtered.length === 0 ? (
        <ZoneEmpty>No items match your filters.</ZoneEmpty>
      ) : (
        <div className="zn-item-grid">
          {filtered.map((h, i) => {
            const img   = getRowImage(h.row, skinsData, inventoryMap);
            const stage = h.type?.color || CANDY[i % CANDY.length];
            const p     = h.diff != null && h.buy > 0 ? h.diff / h.buy * 100 : null;
            const picked = gp.sel.has(h.realIndex);
            return (
              <motion.div key={h.realIndex}
                className={`zn-item${h.locked ? ' on-hold' : ''}${gp.on ? ' picking' : ''}${picked ? ' picked' : ''}`}
                style={{ '--zn-stage': stage }}
                onClick={e => gp.on
                  ? gp.click(h.realIndex, e.shiftKey)
                  : onEdit && onEdit(h.row, h.realIndex)}
                {...rise(i)}>

                <div className="zn-item-stage">
                  {gp.on && <span className="zn-pick">{picked ? '✓' : ''}</span>}
                  <DetailButton className="br" onOpen={() => setDetail({ row: h.row, img })} />
                  {/* Only on items with a trade behind them: the card click opens
                      the editor, so this is the way in to the trade overview. */}
                  {hasTrade(h.row) && (
                    <TradeButton className="br" onOpen={() => setDetail({ row: h.row, img })} />
                  )}
                  {img && <img src={img} alt="" />}
                  {h.settling ? (
                    <span className="zn-item-flag selling" title={`Sold on CSFloat for $${fmt(h.settling.price)}, ${settlesInLabel(h.settling.settlesAt) || 'settling'}`}>
                      {h.settling.settlesAt
                        ? `SOLD ${new Date(h.settling.settlesAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`
                        : 'SOLD'}
                    </span>
                  ) : h.locked && (
                    <span className="zn-item-flag">
                      {h.until
                        ? `LOCK ${new Date(h.until).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`
                        : 'LOCKED'}
                    </span>
                  )}
                  {h.row.float != null && (
                    <span className="zn-item-flag right">{Number(h.row.float).toFixed(6)}</span>
                  )}
                </div>

                <div className="zn-item-body">
                  <div className="zn-item-name" title={h.row.skin.value}>{h.row.skin.value}</div>
                  <div className="zn-item-tags">
                    {h.row.condition?.value && (
                      <span className="zn-tag">{COND_ABBR[h.row.condition.value] || h.row.condition.value}</span>
                    )}
                    {h.type && (
                      <span className="zn-tag" style={{ background: h.type.color, color: readableInk(h.type.color) }}>
                        {h.type.label}
                      </span>
                    )}
                    {/* Crafts only, and only once every price it needs is in */}
                    {h.sp && (() => {
                      const d = describeStickerPct(h.sp);
                      return <span className="zn-tag" title={d.title} style={{ background: ZONE.orange }}>{d.label}</span>;
                    })()}
                    <TradePartnerBadge row={h.row} index={tradePartners} flat />
                  </div>

                  {/* Same strip as the locker. Every card renders it, empty or
                      not, so the grid stays one template: an item with no
                      stickers keeps the space rather than pulling its prices up
                      and standing out next to a craft. */}
                  <StickerSlots stickers={withStickerImages(h.row.stickers, stickerIndex)} charms={h.row.charms}
                    prices={livePrices} variant="zone"
                    hideWhenEmpty={false} blank={!h.row.condition?.value}
                    style={{ marginTop: 9, marginBottom: 10 }} />

                  <div className="zn-item-prices">
                    <div>
                      <div className="k">Paid</div>
                      <div className="v">${fmt(h.buy)}</div>
                    </div>
                    <div>
                      <div className="k">Market</div>
                      <div className="v">{h.mkt != null ? `$${fmt(h.mkt)}` : 'n/a'}</div>
                    </div>
                  </div>

                  <div className={`zn-item-delta ${h.diff == null ? 'flat' : h.diff >= 0 ? 'up' : 'down'}`}>
                    {h.diff == null
                      ? 'no live price'
                      : `${h.diff >= 0 ? '▲' : '▼'} ${signed(h.diff)}${p != null ? ` · ${pct(p)}` : ''}`}
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      )}

      {/* ── Selection action bar — group price, category, or move ─ */}
      {gp.on && (
        <div className="zn-selbar">
          <span className="n">
            {gp.picked.count} selected
            {gp.picked.hidden > 0 ? ` (${gp.picked.hidden} hidden by filter)` : ''}
          </span>
          {gp.picked.count > 0 && (
            <span className="s">
              paid now <strong>${fmt(gp.picked.cost)}</strong>
              {' · '}market <strong>${fmt(gp.picked.market)}</strong>
              {mode === 'move'
                ? (gp.picked.count - movable.invest > 0 ? ` · ${gp.picked.count - movable.invest} already in the vault` : '')
                : mode === 'type'
                  ? ''
                  : (gp.picked.unpriced > 0 ? ` · ${gp.picked.unpriced} without a live price` : '')}
            </span>
          )}
          <button className="zn-btn" onClick={gp.selectAll}>Select all {filtered.length}</button>
          <button className="zn-btn" onClick={gp.clear} disabled={gp.picked.count === 0}>Clear</button>
          {mode === 'type' ? (
            <>
              {/* One pill per category. Filled means every picked item already
                  has it, so clicking takes it off; a partial count is written
                  out so it is clear the click is going to level the rest up. */}
              {(settings?.itemTypes || []).map(t => {
                const n   = tagged[t.value] || 0;
                const all = gp.picked.count > 0 && n === gp.picked.count;
                return (
                  <button key={t.value} className="zn-btn" disabled={gp.picked.count === 0}
                    style={all ? { background: t.color, borderColor: t.color, color: readableInk(t.color) } : undefined}
                    title={all
                      ? `Remove ${t.label} from all ${gp.picked.count}`
                      : `Tag all ${gp.picked.count} as ${t.label}`}
                    onClick={() => applyType(t)}>
                    {all ? '✓ ' : ''}{t.label}
                    {n > 0 && !all ? ` (${n}/${gp.picked.count})` : ''}
                  </button>
                );
              })}
              <button className="zn-btn" disabled={gp.picked.count === 0}
                title="Take every category off the selected items"
                onClick={() => onSetCategory(gp.picked.entries.map(e => e.realIndex), null, 'clear')}>
                Clear all
              </button>
            </>
          ) : mode === 'move' ? (
            <>
              {lendingEnabled && onLendItems && (
                <button className="zn-btn" disabled={movable.lend === 0}
                  title="Record these as lent out — they stay in your totals"
                  onClick={() => {
                    onLendItems(gp.picked.entries.filter(e => !e.row.lend).map(e => e.realIndex));
                    pick(null);
                  }}>
                  → Lending{movable.lend ? ` (${movable.lend})` : ''}
                </button>
              )}
              {onMoveToInvestment && (
                <button className="zn-btn primary" disabled={movable.invest === 0}
                  title="Move these to the Vault as long-term holds — they leave the Armory"
                  onClick={() => {
                    onMoveToInvestment(gp.picked.entries.map(e => e.realIndex));
                    pick(null);
                  }}>
                  → Investments{movable.invest ? ` (${movable.invest})` : ''}
                </button>
              )}
            </>
          ) : (
            <button className="zn-btn primary" disabled={gp.picked.count < 2}
              onClick={() => onGroupPrice(gp.picked.entries.map(e => ({ row: e.row, realIndex: e.realIndex })))}>
              Price {gp.picked.count || ''} item{gp.picked.count === 1 ? '' : 's'}
            </button>
          )}
          <button className="zn-btn" onClick={() => pick(null)}>Done</button>
        </div>
      )}

      <ZoneFooter stickers={[
        { text: 'prices via buff163', bg: ZONE.purple },
        { text: `${holdings.length} in the vault`, bg: ZONE.white },
        { text: `worth $${fmt(totals.value)}`, bg: ZONE.orange, right: true },
      ]} />

      {detail && (
        <ItemDetailModal
          stickerIndex={stickerIndex}
          variant="zone"
          kind="item"
          name={detail.row.skin?.value}
          condition={detail.row.condition?.value}
          image={detail.img}
          history={detail.row.priceHistory}
          livePrices={livePrices}
          csfloatPrices={csfloatPrices}
          row={detail.row}
          rows={rows || []}
          /* the trade overview draws a thumbnail for every item on both sides */
          skinsData={skinsData}
          /* click-through: any item in the trade chain re-centres this modal */
          onOpenRow={r => setDetail({ row: r, img: getRowImage(r, skinsData, inventoryMap) })}
          onClose={() => setDetail(null)}
        />
      )}
    </div>
  );
}
